import React, { memo, useCallback, useEffect, useState } from 'react'
import { Box, Collapse, Flex, Text } from '@chakra-ui/core'
import styled from '@emotion/styled'
import isSolid from 'is-solid'

import colors from 'constants/colors'
import useComments from 'hooks/useComments'
import { getCurrentEpoch } from 'utils'
import { Comment, CommentInput } from '.'
import LocaleText from 'components/LocaleText'
import { IComment, IHandleCreateComment, RootState } from 'constants/interfaces'
import keys from 'constants/keys'
import { useDispatch, useSelector } from 'react-redux'
import { setCommentsCounter } from 'redux/actions/comments'
import CommentsContentLoader from 'components/Comments/CommentsContentLoader'

const CommentsWrapper = styled(Box)``

interface IProps {
  postID: string
  postAuthorID: string
  rasID: string
  showCommentsSection: boolean
  isInputFocus: boolean
  updatedCommentsCounter: number
  isDoublePost?: boolean
  postType?: number
  hideInput?: boolean
  isPersonalViewMode?: boolean
  targetParentCommentId?: string | null
  targetCommentId?: string | null
}

const PostCommentsContainer: React.FC<IProps> = ({
  postID,
  postAuthorID,
  rasID,
  showCommentsSection,
  isInputFocus,
  isDoublePost,
  postType,
  hideInput = false,
  isPersonalViewMode = false,
  targetParentCommentId,
  targetCommentId,
}: IProps) => {
  const dispatch = useDispatch()
  const {
    fetchComments,
    sendComment,
    sendCommentLoading,
    fetchCommentsLoading,
    isLoadMore,
    fetchCommentsCounter,
    comments,
    commentsCounter,
  } = useComments(postID)
  const [showAllComments, setShowAllComments] = useState<boolean>(true)
  const [offset, setOffset] = useState<number>(0)
  const [limit, setLimit] = useState<number>(2)
  const [isFocused, setFocused] = useState<boolean>(false)
  const { commentsCounter: commentsCounterState } = useSelector((state: RootState) => state.comments)

  const getComments = useCallback(
    async (isManuallyTriggered?: boolean, id?: string) => {
      try {
        let timestampFromToGetComments = getCurrentEpoch()

        if (comments.length > 0) {
          timestampFromToGetComments = comments[0].created_at_ts
        }
        const commentsFromOrigin = await fetchComments({
          offset,
          limit,
          created_ts: timestampFromToGetComments,
          isManuallyTriggered,
          id,
        })
        if (!isSolid(commentsFromOrigin)) {
          return
        }

        setOffset(offset + limit)
        setLimit(5)
      } catch (error) {
        console.log(error)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [comments, limit, offset]
  )

  const handleGetMoreComments = useCallback(async () => {
    if (!fetchCommentsLoading) getComments(true, postID)
  }, [getComments, postID, fetchCommentsLoading])

  const handleCreateComment = async ({ comment, images, videos, audios, files }: IHandleCreateComment) => {
    const isResponseOk = await sendComment({
      id: postID,
      comment,
      rasID,
      createdByUserId: postAuthorID,
      images,
      videos,
      audios,
      files,
      objectType: keys.COMMENT_OBJECT_TYPE.COMMNET_ON_POST,
      ...(postType === keys.POST_TYPE.GROUP_POST ? { is_update_post: true } : {}),
    })
    if (isResponseOk) dispatch(setCommentsCounter({ count: commentsCounter + 1, key: postID }))
  }

  useEffect(() => {
    if (comments.length > 0 || isDoublePost || isPersonalViewMode) return
    getComments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postID])

  useEffect(() => {
    if (isDoublePost || (isPersonalViewMode && postID in commentsCounterState)) return
    fetchCommentsCounter()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postID])

  useEffect(() => {
    setFocused(isInputFocus)
  }, [isInputFocus])

  return (
    <Collapse className="post-comments-collapse" isOpen={showCommentsSection}>
      <CommentsWrapper className="post-comments-container">
        {fetchCommentsLoading && comments.length === 0 && (
          <Box maxH="min(60vh, 480px)" overflowY="auto" aria-busy="true">
            <CommentsContentLoader />
          </Box>
        )}

        {comments.length > 0 && (
          <>
            {isLoadMore && !fetchCommentsLoading && (
              <Flex onClick={handleGetMoreComments} color={colors.greyDark} cursor="pointer" mb="5px">
                <Text>
                  <LocaleText text="fetch_more_comments_post_button" />
                </Text>{' '}
              </Flex>
            )}

            {fetchCommentsLoading && <CommentsContentLoader count={limit} />}

            {!isLoadMore && comments.length > 2 && (
              <Box
                onClick={() => setShowAllComments(!showAllComments)}
                color={colors.greyDark}
                cursor="pointer"
                mb="5px"
              >
                {showAllComments ? (
                  <Text>
                    <LocaleText text="hide_comments_post_button" />
                  </Text>
                ) : (
                  <Text>View all {comments.length} comments</Text>
                )}
              </Box>
            )}

            {isSolid(comments) && (
              <Collapse startingHeight={90} isOpen={showAllComments}>
                {comments.map((comment: IComment) => (
                  <Comment
                    key={comment.cid}
                    {...comment}
                    postID={postID}
                    authorID={postAuthorID}
                    rasID={rasID}
                    isDoublePost={isDoublePost}
                    postType={postType}
                    targetParentCommentId={targetParentCommentId}
                    targetCommentId={targetCommentId}
                    shouldOpenReplies={targetParentCommentId === comment.cid}
                  />
                ))}
              </Collapse>
            )}
          </>
        )}

        {!hideInput && (
          <CommentInput
            isFocused={isFocused}
            postID={postID}
            handleCreateComment={handleCreateComment}
            sendCommentLoading={sendCommentLoading}
          />
        )}
      </CommentsWrapper>
    </Collapse>
  )
}

export default memo(PostCommentsContainer)
